import { Component, ElementRef, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { IDsBreadcrumbItem } from '@levelaccess/design-system';

import { $salesforceAccount } from '@shared/constants/salesforce';
import { SharedCommonUtility } from '@shared/utils/common.utility';
import { errorMessagesNames } from '@shared/constants/errors';
import { IPackage } from '@shared/interfaces/packaging.interface';
import { $package, PackageNames } from '@shared/constants/packaging';
import { ISalesforceAccount } from '@shared/interfaces/salesforce.interface';
import { $academy } from '@shared/constants/academy';
import { $tenant, $tenantMetadata, $tenants, CHAR_LIMITS } from '@shared/constants/tenant';
import { ICreateTenantRequest, ITenant, ITenantsResponse } from '@shared/interfaces/tenant.interface';
import { ErrorMessageService } from '../../../services/error-message.service';
import { TenantService } from '../../../services/tenant.service';
import { A11yService } from '../../../services/a11y.service';
import { NotificationService } from '../../../services/notification.service';
import { CommonUtility } from '../../../utility/common.utility';
import { PackageService } from '../../../services/package.service';
import { ModalService } from '../../../services/modal.service';
import { CreateTenantConfirmationModalComponent } from './create-tenant-confirmation-modal/create-tenant-confirmation-modal.component';
import { AbstractTenantComponent, FormFields } from '../upsert-tenant/abstract-tenant.component';
import { CustomValidators } from '../../../services/helpers/form-custom-validators';
import { ErrorHandlerService } from '../../../services/error-handler.service';
import { AppConfigService } from '../../../services/app-config.service';
import { RoutingService } from '../../../services/routing.service';
import { TranslateService } from '../../../translate/translate.service';
import { FeatureFlagService } from '../../../services/feature-flag/feature-flag.service';
import { AngularUtility } from '../../../utility/angular.utility';
import { UsageLimitFieldContext } from '../upsert-tenant/usage-limit-component/usage-limit.component';

@Component({
  selector: 'app-create-tenant',
  templateUrl: '../upsert-tenant/upsert-tenant.component.html',
  styleUrls: ['../upsert-tenant/upsert-tenant.component.scss'],
  standalone: false,
})
export class CreateTenantComponent extends AbstractTenantComponent implements OnInit {
  public doneLoading: boolean = true;

  public domainFieldEditable: boolean = true;
  public errorMessage: string = 'create_tenant_error';

  public breadcrumbs: IDsBreadcrumbItem[];

  private _displayPackages$: Observable<IPackage[]>;
  get displayPackages$(): Observable<IPackage[]> {
    return this._displayPackages$;
  }

  constructor(
    routingService: RoutingService,
    formBuilder: UntypedFormBuilder,
    appConfigService: AppConfigService,
    errorMessageService: ErrorMessageService,
    notificationService: NotificationService,
    tenantService: TenantService,
    translateService: TranslateService,
    a11yService: A11yService,
    packageService: PackageService,
    featureFlagService: FeatureFlagService,
    router: Router,
    private errorHandlerService: ErrorHandlerService,
    private element: ElementRef<Element>,
    private modalService: ModalService,
  ) {
    super(
      routingService,
      formBuilder,
      appConfigService,
      errorMessageService,
      notificationService,
      tenantService,
      translateService,
      a11yService,
      packageService,
      featureFlagService,
      router,
    );
  }

  private onCreateTenantSuccess(): void {
    this.navigateToAdminTenantsPage();
  }

  private onCreateTenantError(response: HttpErrorResponse): void {
    if (CommonUtility.extractHTTPErrorName(response) === errorMessagesNames.AcademyInitializationFailed) {
      this.onCreateTenantSuccess();
      return;
    }

    this.errorHandlerService.handleError(response);
    this.handleError('error_create_tenant', response);
  }

  private confirmAndCreate(createRequest: ICreateTenantRequest): void {
    const modal: CreateTenantConfirmationModalComponent = this.modalService.open(CreateTenantConfirmationModalComponent).instance;
    modal.context = {
      tenantDetails: createRequest,
      packageName: this.form.get(FormFields.package).value[$package.name],
      tenantBaseUrl: this.tenantBaseUrl,
    };

    this.subscription.add(
      modal.container.onClose$
        .pipe(
          take(1),
          mergeMap((): Observable<ITenant | void> => {
            if (modal.confirmed) {
              return this.tenantService.createTenant(createRequest).pipe(
                tap((): void => {
                  this.onCreateTenantSuccess();
                }),
                catchError((error: HttpErrorResponse): Observable<void> => {
                  this.onCreateTenantError(error);
                  return of(void 0);
                }),
              );
            }

            return of(void 0);
          }),
        )
        .subscribe(),
    );
  }

  private navigateToAdminTenantsPage(): void {
    this.router
      .navigate([this.tenantRoutePath])
      .catch(this.errorHandlerService.handleRoutingError.bind(this.errorHandlerService));
  }

  public onClose(): void {
    this.navigateToAdminTenantsPage();
  }

  public async submit(): Promise<void> {
    this.errorResponseMessagesSet.clear();
    this.formValidationRequest.next();

    if (this.form.valid === false) {
      this.errorMessageService.setFocusOnFirstError(this.element.nativeElement);
      return;
    }

    const showInternalNotes: boolean = this.featureFlagService.variation(
      this.FeatureFlagCollection.showInternalNotesHideDescription,
      false,
    );

    const createRequest: ICreateTenantRequest = {
      ...this.formTenantNameAndSalesforceAccount,
      [$tenant.domain]: this.form.get(FormFields.domain).value.trim(),
      ...(showInternalNotes
        ? { [$tenant.internalNotes]: this.form.get(FormFields.internalNotes).value?.trim() }
        : { [$tenant.description]: this.form.get(FormFields.description).value?.trim() }),
      [$tenant.pdfPagesTestLimit]: Number(this.form.get(FormFields.pdfPagesTestLimit).value),
      [$tenant.monitoringPagesPerDigitalPropertyLimit]: Number(
        this.form.get(FormFields.monitoringPagesPerDigitalPropertyLimit).value,
      ),
      [$academy.seatLimit]: Number(this.form.get(FormFields.seatLimit).value),
      [$tenant.digitalPropertyLimit]: Number(this.form.get(FormFields.digitalPropertyLimit).value),
      [$tenantMetadata.packageId]: this.form.get(FormFields.package).value[$package._id],
      ...(SharedCommonUtility.notNullishOrEmpty(this.getFilteredPackageAddons())
        ? { [$tenantMetadata.packageAddonIds]: this.getFilteredPackageAddons() }
        : {}),
      ...this.formTenantArtificialIntelligence,
      ...this.formTenantPrivacySettings,
      ...this.formTenantScanningAgentDebugMode,
      [$tenant.scanOptions]: this.form.get(FormFields.scanOptions)?.value,
    };

    const resolvedExternalPackage = this.resolveExternalPackageSignupFieldForRequest();
    if (SharedCommonUtility.notNullish(resolvedExternalPackage)) {
      createRequest[$academy.externalPackageSignupField] = resolvedExternalPackage;
    }

    const dpComplexityCounts = this.showDpComplexityCalculator ? this.form.get(FormFields.dpComplexityCounts)?.value : null;
    createRequest[$tenant.dpComplexityCounts] = dpComplexityCounts ?? null;

    const scanDefaultTimeoutHoursValue: number | null = this.form.get(FormFields.scanDefaultTimeoutHours)?.value;

    createRequest[$tenant.scanDefaultTimeoutHours] = SharedCommonUtility.notNullish(scanDefaultTimeoutHoursValue)
      ? Number(scanDefaultTimeoutHoursValue)
      : null;

    if (this.showUserWayWebsiteLimit) {
      createRequest[$tenant.userWayWebsiteLimit] = Number(this.form.get(FormFields.userWayWebsiteLimit).value);
    }

    this.confirmAndCreate(createRequest);
  }

  public getPdfPagesTestInputContext(): UsageLimitFieldContext {
    return {
      label: 'label_pdf_pages',
      field: FormFields.pdfPagesTestLimit,
      tooltip: 'total_pdf_tests_tooltip',
      maxLimit: this.maxPdfPagesTestLimit,
    };
  }

  public searchSalesforceAccounts = (term$: Observable<string>): Observable<ISalesforceAccount[]> =>
    term$.pipe(
      filter(SharedCommonUtility.notNullishOrEmpty),
      withLatestFrom(this.salesforceAccounts$),
      map(([term, accounts]: [string, ISalesforceAccount[]]) =>
        accounts.filter((account: ISalesforceAccount) =>
          account[$salesforceAccount.name].toLowerCase().includes(term.toLowerCase()),
        ),
      ),
    );

  public ngOnInit(): void {
    super.ngOnInit();

    this.form.addControl(
      FormFields.domain,
      this.formBuilder.control(null, [
        CustomValidators.required,
        Validators.maxLength(CHAR_LIMITS[$tenant.domain]),
        CustomValidators.validateSubdomain,
      ]),
    );

    this._displayPackages$ = combineLatest([
      this.packages$,
      this.featureFlagService.variation$(this.FeatureFlagCollection.createLegacyEA, false),
      this.featureFlagService.variation$(this.FeatureFlagCollection.auditOnlyPackage, false),
    ]).pipe(
      map(([packages, createLegacyEAFlag, auditOnlyPackageFlag]: [IPackage[], boolean, boolean]): IPackage[] => {
        const packageNamesToRemove: string[] = [];

        if (!createLegacyEAFlag) {
          packageNamesToRemove.push(PackageNames.LegacyEA);
        }

        if (!auditOnlyPackageFlag) {
          packageNamesToRemove.push(PackageNames.Audit);
        }

        return packages.filter((packageInstance: IPackage) => !packageNamesToRemove.includes(packageInstance[$package.name]));
      }),
      AngularUtility.shareRef(),
    );

    this.addUniqueDomainValidator();
    this.setupBreadcrumbs();
  }

  private addUniqueDomainValidator(): void {
    const control: AbstractControl = this.form.get(FormFields.domain);

    this.subscription.add(
      this.otherTenants$.subscribe((otherTenants: ITenant[]) => {
        const existingDomains: string[] = otherTenants.map((tenant: ITenant) => tenant[$tenant.domain]);

        control.addValidators(CustomValidators.uniqueValidator(new Set(existingDomains), true));
        control.updateValueAndValidity();
      }),
    );
  }

  protected getOtherTenants$(): Observable<ITenant[]> {
    return this.tenantService.getTenants().pipe(
      map((response: ITenantsResponse) => response[$tenants.tenants]),
      AngularUtility.shareRef(),
    );
  }

  private setupBreadcrumbs(): void {
    this.breadcrumbs = [
      {
        label: this.translateService.instant('tenants'),
        rtLink: this.tenantRoutePath,
      },
      {
        label: this.translateService.instant('create_a_tenant'),
      },
    ];
  }
}
